import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AttachmentType, ClockService, Complaint, ComplaintAppointment, ComplaintData, DocumentAttachment, editableAppointmentStatuses, PersonsService } from '@icc/b2b/shared';
import { ComplaintService } from '../complaint.service';
import { ConfigService, core } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'icc-complaint-appointment-edit',
    templateUrl: './complaint-appointment-edit.component.html',
    styleUrls: ['./complaint-appointment-edit.component.scss'],
})
export class ComplaintAppointmentEditComponent implements OnInit {
    @Input() complaintAppointment!: ComplaintAppointment;
    @Input() complaint!: Complaint;
    @Input() complaintAppointmentAttachments: DocumentAttachment[] = [];
    @Input() editMode!: boolean;
    @Output() validDate = new EventEmitter<boolean>();

    activeUserCompany = Number(this.configService?.user?.user?.ActiveCompany);

    data: ComplaintData = {} as ComplaintData;

    appointmentStatuses = editableAppointmentStatuses;

    workersList;
    appointmentResponsibleUser: number | null = null;

    minDate = new Date();

    initialDate: Date;

    constructor(
        private complaintService: ComplaintService,
        private personsService: PersonsService,
        private configService: ConfigService,
        private toastService: ToastService,
        private translateService: TranslateService
    ) {
        this.data = this.complaintService.data;
    }

    ngOnInit(): void {
        this.workersList = this.personsService.workersList;
        this.complaintAppointment.assignedCompanyId = this.activeUserCompany;
        if (new Date(this.complaintAppointment.date).setHours(0, 0, 0, 0) > this.minDate.setHours(0, 0, 0, 0)) {
            this.initialDate = this.minDate;
        } else {
            this.initialDate = this.complaintAppointment.date;
        }
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    getAssignedTo() {
        return this.activeUserCompany;
    }

    isDateInvalid(date): boolean {
        if (date) {
            const appointmentDate = new Date(date);
            const isValidDate = appointmentDate.setHours(0, 0, 0, 0) < this.minDate.setHours(0, 0, 0, 0);
            this.validDate.emit(isValidDate);
            return isValidDate;
        }
        return false;
    }

    isDateInvalidEditMode(date): boolean {
        if (date) {
            const newAppointmentDate = new Date(date);
            const isValidDate = newAppointmentDate.setHours(0, 0, 0, 0) < new Date(this.initialDate).setHours(0, 0, 0, 0);
            this.validDate.emit(isValidDate);
            return isValidDate;
        }
        return false;
    }

    onDateChange(date) {
        if (!this.isDateInvalidEditMode(date)) {
            this.complaintAppointment.date = date;
        }
    }

    async onAddAttachment(files: FileList, type: DocumentAttachment['attachmentType']) {
        const maxSizeInMB = 7;
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        const maxFiles = 5;

        if (files[0]) {
            if (files[0].size > maxSizeInBytes) {
                this.toastService.add({
                    severity: 'error',
                    title: this.translateService.instant('COMPLAINT|Plik jest zbyt duży.'),
                    body: this.translateService.instant('COMPLAINT|Maksymalna wielkość to {maxSizeInMB}MB.', { maxSizeInMB }),
                });
                return;
            }

            if (type === 'appointmentAttachment' && this.complaintAppointmentAttachments.length >= maxFiles) {
                this.toastService.add({
                    severity: 'warn',
                    title: this.translateService.instant('COMPLAINT|Osiągnięto limit zdjęć.'),
                    body: this.translateService.instant('COMPLAINT|Możesz dodać maksymalnie {maxFiles} zdjęć.', { maxFiles }),
                });
                return;
            }

            const data = await core.blobToArrayBuffer(files[0]);
            const newAttachment: DocumentAttachment = {
                uuid: core.generateUUID(),
                type: 'complaint',
                documentId: this.complaint?.uuid,
                appointmentId: this.complaintAppointment?.uuid,
                fileType: files[0].type,
                created: new Date(),
                modified: new Date(),
                machineId: ClockService.getClock().timestamp.node(),
                name: files[0].name,
                data,
                attachmentType: type,
                description: '',
            };
            await this.complaintService.cacheAttachment(newAttachment.uuid, files[0]);
            this.complaintAppointmentAttachments.push(newAttachment);
        }
    }

    removeAttachment(type: DocumentAttachment['attachmentType'], uuid: string) {
        const attachment = this.complaintAppointmentAttachments.find((el) => el.attachmentType === type && el.uuid === uuid);
        if (attachment) {
            (document.getElementById(type + this.complaintAppointment.uuid) as HTMLInputElement).value = '';
            this.complaintAppointmentAttachments.splice(this.complaintAppointmentAttachments.findIndex(
                (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
            ), 1);
        }
    }

    getAttachments(type: AttachmentType): DocumentAttachment[] {
        return this.complaintAppointmentAttachments.filter(
            (attachment) => attachment.attachmentType === type
        );
    }

    getAttachment(type: DocumentAttachment['attachmentType'], uuid?: string) {
        const attachment = this.complaintAppointmentAttachments.find(
            (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
        );
        return attachment;
    }
}
